// ====================================================================================
// UTILITY COMPONENTS
// ====================================================================================

import { motion } from "framer-motion";

import {BadgeCheck, ChevronRight, TrendingUp, Zap, TrendingDown, AlertTriangle, GitMerge, Eye, Shield, ShieldCheck, Crosshair, Home, Globe, Megaphone, Info, CheckCircle, X, FileWarning } from "lucide-react"
export const Panel = ({ title, icon: Icon, children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`bg-slate-800/50 border border-slate-700/50 rounded-xl shadow-xl ${className}`}
  >
    {title && (
      <h3 className="flex items-center text-lg font-semibold text-white mb-4 px-6 pt-6">
        {Icon && <Icon size={20} className="mr-2 text-accent-400" />}
        <span className="ml-0">{title}</span>
      </h3>
    )}
    <div className={title ? "px-6 pb-6" : "p-6"}>{children}</div>
  </motion.div>
);

export const ValueBadge = ({ value, type = "" }) => {
  const configs = {
    // Labels
    "Very High": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: TrendingUp },
    "High": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: TrendingUp },
    "Moderate": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Zap },
    "Low": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: TrendingDown },
    "Very Low": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: TrendingDown },
    
    // Strategic Tiers
    "Priority: Gain": { color: 'bg-yellow-500 border-yellow-400 text-yellow-900', icon: TrendingUp },
    "Target: Gain": { color: 'bg-yellow-400 border-yellow-300 text-yellow-900', icon: Zap },
    "Critical: Defend": { color: 'bg-red-600 border-red-500 text-white', icon: AlertTriangle },
    "Urgent: Defend": { color: 'bg-orange-500 border-orange-400 text-white', icon: ShieldCheck },
    "Priority: Defend": { color: 'bg-sky-500 border-sky-400 text-white', icon: Shield },
    "Long-Term Target": { color: 'bg-blue-400 border-blue-300 text-white', icon: Crosshair },
    "Safe Hold": { color: 'bg-green-500 border-green-400 text-white', icon: CheckCircle },
    "Unwinnable": { color: 'bg-slate-600 border-slate-500 text-white', icon: X },
    "Watch List": { color: 'bg-purple-500 border-purple-400 text-white', icon: Eye },
    "No Contest": { color: 'bg-purple-500 border-purple-400 text-white', icon: FileWarning },
    
    // Ward DNA Badges
    "Economically Secure": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Shield },
    "Economically Stable": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: ShieldCheck },
    "Economically Precarious": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: AlertTriangle },
    "High Owner-Occupancy": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Home },
    "Majority Owner-Occupied": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: Home },
    "High Diversity": { color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: Globe },
    "Moderate Diversity": { color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20', icon: Globe },
    "Low Diversity": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Globe },
    
    // Archetype Leaning Badges
    "Progressive Left": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Zap },
    "Progressive Left (Single-Issue Focus)": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Zap },
    "Progressive Left (Radical)": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Zap },
    "Centre-Left": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: GitMerge },
    "Centrist / Centre-Left": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: GitMerge },
    "Centrist / Liberal": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: GitMerge },
    "Centre-Right (Traditional)": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Shield },
    "Disengaged / Apolitical": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Eye },
    "Disengaged / Centrist": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Eye },
    "Populist Right": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: Megaphone },
    "Authoritarian Right / Traditionalist": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: Shield },
    "Right-Leaning / Disengaged": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Eye },
    "Authoritarian Right / Traditional Conservative": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: Shield },
    "Populist Right / Anti-Establishment": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: Megaphone },
    "Disengaged / Anti-Establishment": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: Eye },
    "Economically Populist / Volatile": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Zap },
    
    "Unknown": { color: 'bg-gray-700 border-gray-600 text-white', icon: Info }
  };
  const label = value || "Unknown";
  const config = configs[label] || configs[label.toUpperCase()] || configs.Unknown;
  const Icon = config.icon;
  return (
    <div className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon size={12} />
      <span className="">{label}</span>
    </div>
  );
};

export const StatCard = ({ title, value, icon: Icon, colorClass = "text-white" }) => (
  <div className="flex flex-col items-start bg-slate-700/30 rounded-lg p-4 transition-all hover:bg-slate-700/50">
    <div className="flex items-center mb-1">
      <Icon size={16} className={`mr-2 ${colorClass}`} />
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</span>
    </div>
    <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
  </div>
);

export const ArchetypeCard = ({ archetypeData, onClick, isPrimaryTarget = false, style = {} }) => {
  if (!archetypeData) return null;

  // --- START NCP CALCULATION FOR DISPLAY ---
  const population = archetypeData?.ward_representation_percentage || 0;
  const persuadability = archetypeData?.campaign_targeting?.persuadability_score || 0;
  const activationDifficulty = archetypeData?.campaign_targeting?.activation_difficulty || 0;
  
  // NCP Score = (Population * Persuadability) * (1 - Activation)
  const ncpScore = (population * persuadability) * (1 - activationDifficulty);
  // --- END NCP CALCULATION FOR DISPLAY ---

  return (
    <div 
      onClick={onClick} 
      style={style} 
      className="relative border border-slate-700/50 rounded-xl shadow-lg p-4 hover:border-slate-600 transition-colors duration-200 cursor-pointer flex flex-col h-full group"
    >
      {isPrimaryTarget && (
        <div className="absolute top-2 right-2 flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-500/10 text-success-400 border border-success-500/20">
          <BadgeCheck size={14} />
          <span>Primary Target</span>
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <h4 className="font-semibold text-white text-sm">{archetypeData?.name || 'Unnamed Archetype'}</h4>
        </div>
         <ChevronRight size={16} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
      </div>
      <p className="text-xs text-slate-400 mb-4 leading-relaxed flex-grow">{archetypeData?.description || ""}</p>
      <div className="flex items-center justify-between mb-4 mt-4">
          <span className="text-sm font-semibold text-white">
            Est. {archetypeData?.population_metrics?.estimated_population?.toLocaleString() || 0} voters
          </span>
      </div>
      
      {/* NCP DISPLAY: 3-COLUMN LAYOUT */}
      <div className="grid grid-cols-3 gap-2 text-xs text-center mb-4">
        <div>
          <div className="font-bold text-white text-base">
            {(persuadability * 100).toFixed(0)}%
          </div>
          <div className="text-slate-400 text-2xs">Persuadable</div>
        </div>

        <div>
          <div className="font-bold text-white text-base">
            {(activationDifficulty * 100).toFixed(0)}%
          </div>
          <div className="text-slate-400 text-2xs">Activation Difficulty</div>
        </div>

        <div className="border-l border-slate-700/50">
          <div className="font-bold text-accent-400 text-base">
            {ncpScore.toFixed(1)}
          </div>
          <div className="text-slate-400 text-2xs">Net Converstion Potential</div>
        </div>
      </div>
    </div>
  );
};

// SCRIPT TAGGING
export const ScriptTag = ({ text, color, icon: Icon }) => {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  };
  return (
    <div className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors[color]}`}>
      <Icon size={14} />
      <span>{text}</span>
    </div>
  );
};